import { isOpportunityId } from '../opportunity';

/**
 * A saved list written into a link (contract §18): the calendar feed's path
 * and the shareable link's `ids=`. Ids comma-separated, Going as they are and
 * Maybe with `.m` after them — characters a URL leaves alone, so the link
 * reads as it is written:
 *
 *   nyc-weact-7994d16f,nyc-nycparks-74ad08ef.m
 *
 * Anything that isn't an id is dropped on the way in, so a hand-edited or
 * truncated link still opens with what it can.
 */

export type SavedStatus = 'going' | 'maybe';

const MAYBE = '.m';

/** In id order, so the same list always makes the same link. */
export function encodeSavedList(entries: Iterable<readonly [string, SavedStatus]>): string {
  return [...entries]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([id, status]) => (status === 'maybe' ? `${id}${MAYBE}` : id))
    .join(',');
}

export function decodeSavedList(text: string | null | undefined): Map<string, SavedStatus> {
  const out = new Map<string, SavedStatus>();
  for (const raw of (text ?? '').split(',')) {
    const part = raw.trim();
    const maybe = part.endsWith(MAYBE);
    const id = maybe ? part.slice(0, -MAYBE.length) : part;
    if (isOpportunityId(id) && !out.has(id)) out.set(id, maybe ? 'maybe' : 'going');
  }
  return out;
}

/** The city an id belongs to: its first part (`nyc-weact-…` → `nyc`). */
export const cityOfId = (id: string): string => id.slice(0, id.indexOf('-'));
